import sortBy from 'lodash.sortby';
import { EPG_GENRES, COLLECTIONS } from '@te_react/volt-store/lib/constants';
import { isWeb } from '../../utils';

const getChannelsState = (s: any, isKidProfile: any, kidsChannels: any, productsData: any) => {
    const channelsData = s.channels.data;
    const channelsFilteringCriteria = s.channels.filteringCriteria;

    return Object.values(channelsData).reduce((acc: any, channel: any) => {
        if (isKidProfile) {
            if (kidsChannels?.length > 0) {
                if (channel.isKidsChannel) {
                    acc[channel.id] = channel;
                }
                return acc;
            }
            if (isChannelWatchable(channel, productsData)) {
                acc[channel.id] = channel;
            }
        } else {
            if (
                !channel.channelFilter ||
                !channelsFilteringCriteria ||
                !channelsFilteringCriteria.length ||
                channelsFilteringCriteria.includes(channel.channelFilter)
            ) {
                acc[channel.id] = channel;
            }
        }
        return acc;
    }, {});
};

export const getSelectedChannelsSelector = (s: any) => {
    const isKidProfile = s.profile?.isKidProfile;
    const kidsChannels = isKidProfile ? s.channels.filter?.((ch: any) => ch?.isKidsChannel) : [];
    const productsData = s.products.data;

    const channelsState = getChannelsState(s, isKidProfile, kidsChannels, productsData);
    const genre = s.epg.genre;

    if (!channelsState) return [];

    const channelList = sortBy(Object.values(channelsState), (x: any) => x.channelNumber);
    const favoriteChannels = channelList.filter((channel: any) => channel.favorite);

    let filteredChannelList;
    if (!genre) {
        filteredChannelList = channelList.filter((channel: any) => !channel.isMosaicChannel());
    } else if (genre === EPG_GENRES.FAVORITES) {
        filteredChannelList = favoriteChannels;
    } else if (genre === EPG_GENRES.SUBSCRIBED) {
        filteredChannelList = favoriteChannels.filter((channel: any) => isChannelWatchable(channel, productsData));
    } else {
        filteredChannelList = channelList.filter(
            (channel: any) => channel.genres.includes(genre) && !channel.isMosaicChannel()
        );
    }

    return filteredChannelList;
};

export const getGuideDataSelector = (s: any, skipGenreFilter = false) => {
    const isKidProfile = s.profile?.isKidProfile;
    const kidsChannels = isKidProfile ? s.channels.filter?.((ch: any) => ch?.isKidsChannel) : [];
    const productsData = s.products.data;

    const channelsState: any = getChannelsState(s, isKidProfile, kidsChannels, productsData);

    if (!channelsState) return {};

    const channelList = sortBy(Object.values(channelsState), (x: any) => x.channelNumber);
    const genre = s.epg.genre;
    const favoriteChannels = channelList.filter((channel: any) => channel.favorite);

    let filteredChannelList;
    // Not checking for genre when in player as we need to show mini epg, channel tunning and channel by number for all channels without genre filter.
    if (!genre || (skipGenreFilter && !isWeb)) {
        filteredChannelList = channelList.filter((channel: any) => !channel.isMosaicChannel());
    } else if (genre === EPG_GENRES.FAVORITES) {
        filteredChannelList = favoriteChannels;
    } else if (genre === EPG_GENRES.SUBSCRIBED) {
        filteredChannelList = favoriteChannels.filter((channel: any) => isChannelWatchable(channel, productsData));
    } else {
        filteredChannelList = channelList.filter(
            (channel: any) => channel.genres.includes(genre) && !channel.isMosaicChannel()
        );
    }

    const filteredChannelIds = new Set(filteredChannelList.map(({ id }: any) => id));

    const collection = s.collections[COLLECTIONS.liveNow];
    if (!collection) return {};

    const selectLiveNow = collection.createPopulatedCopy((programId: any) => {
        const program = s.programs[programId];
        return isLiveContent(program) ? program : channelsState[programId];
    });

    const result = Object.assign({}, selectLiveNow);
    result.children = selectLiveNow.children.filter(
        (liveNowProgram: any) =>
            (liveNowProgram.channelId && filteredChannelIds.has(liveNowProgram.channelId)) ||
            (liveNowProgram.id && filteredChannelIds.has(liveNowProgram.id))
    );

    return result;
};

const isLiveContent = (program: any) => program?.isLive?.() || program?.isVirtualContent?.();

export const isChannelWatchable = (channel: any, products: any) =>
    (channel.isVirtualChannel() && !!channel.videoUrl) ||
    (!channel.isVirtualChannel() && channel.productIds.length === 0) ||
    getProductsFromChannel(channel, products).some((product: any) => product.isEntitledNow());

export const getProductsFromChannel = (channel: any, products: any) =>
    channel.productIds.reduce((acc: any, productId: any) => {
        const product = products[productId];
        if (product?.isSubscription()) {
            acc.push(product);
        }
        return acc;
    }, []);
